import AccountStatus from "enums/AccountStatus";
import { IntegrationStatus } from "enums/IntegrationStatus";
import { ExternalAccount } from "hooks/useGetAccounts";

/**
 * Where a supported integration stands for this company. Distinct from
 * `IntegrationStatus`, which says whether the integration exists at all.
 */
export enum ConnectionStatus {
  CONNECTED = "Connected",
  NOT_CONNECTED = "Not Connected",
  CONNECTING = "Connecting",
  DISCONNECTING = "Disconnecting",
  DISCONNECTED = "Disconnected",
}

export interface IntegrationEntity {
  entityKey: string;
  entityName: string;
}

export interface Integration {
  integrationType: string;
  integrationService: string;
  sourceSystem: string;
  group: string;
  title: string;
  url: string;
  status: string;
  /** The entities this integration can carry an allocation type on. */
  settings?: { integrationEntitySettings?: IntegrationEntity[] | null } | null;
}

export interface IntegrationConnection extends Integration {
  connectionStatus?: ConnectionStatus;
  accountId?: string;
  accountName?: string;
}

/** Group keys the API returns, mapped to keys the catalogue actually holds. */
export const INTEGRATION_GROUP_LABEL_KEY: Record<string, string> = {
  accounting: "Accounting",
  jobManagement: "Job Management",
};

const isCatalogueOnly = (integration: Integration) =>
  [IntegrationStatus.SUPPORTED, IntegrationStatus.INACTIVE].includes(
    integration.status as IntegrationStatus,
  );

/**
 * Folds the account list into the integration catalogue.
 *
 * `pending` carries the optimistic status of an in-flight connect or
 * disconnect. It is applied here rather than written into the list, because the
 * list is derived from two queries that refetch and would overwrite it.
 */
export const mergeConnections = (
  integrations: Integration[],
  accounts: ExternalAccount[],
  pending: Record<string, ConnectionStatus> = {},
): IntegrationConnection[] =>
  integrations.map((integration) => {
    if (isCatalogueOnly(integration)) return integration;

    const matching = accounts.filter(
      (candidate) => candidate.accountType === integration.integrationType,
    );
    // A live row outranks a disconnected one an earlier connect left beside it.
    const account =
      matching.find((candidate) => candidate.status === AccountStatus.ACTIVE) ??
      matching[0];

    const settled =
      account?.status === AccountStatus.ACTIVE
        ? ConnectionStatus.CONNECTED
        : account?.status === AccountStatus.DISCONNECTED
          ? ConnectionStatus.DISCONNECTED
          : ConnectionStatus.NOT_CONNECTED;

    return {
      ...integration,
      connectionStatus: pending[integration.integrationType] ?? settled,
      accountId: account?.id,
      accountName: account?.name,
    };
  });

/** Has an account behind it, whatever state that account is in. */
export const isConnected = (connection: IntegrationConnection) =>
  connection.connectionStatus === ConnectionStatus.CONNECTED ||
  connection.connectionStatus === ConnectionStatus.DISCONNECTING ||
  connection.connectionStatus === ConnectionStatus.DISCONNECTED;

export const isBrowsable = (connection: IntegrationConnection) =>
  !isConnected(connection);

export const byTitle = (a: Integration, b: Integration) =>
  a.title.localeCompare(b.title);

/** Sorts the not-yet-available integrations to the end, then by title. */
export const comingSoonLast = (a: Integration, b: Integration) => {
  const aSupported = a.status === IntegrationStatus.SUPPORTED;
  const bSupported = b.status === IntegrationStatus.SUPPORTED;
  if (aSupported !== bSupported) return aSupported ? 1 : -1;
  return byTitle(a, b);
};

export const matchesQuery = (connection: Integration, query: string) => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return true;
  return connection.title.toLowerCase().includes(trimmed);
};

/** How the server spells an allocation type that is deliberately unmapped. */
const NOT_MAPPED = "none";

export interface MappedEntity {
  integrationSourceSystem: string;
  integrationEntity: string;
}

/**
 * How much of what this integration can map is mapped.
 *
 * The denominator is the entities the integration exposes, not the number of
 * allocation types, because the settings drawer spends each entity once: two
 * entities is the most QuickBooks Online can ever carry. Returns null when
 * there is nothing to count, so the card can stay quiet rather than claim a
 * zero it cannot see (allocations are feature gated).
 */
export const entityMappingCount = (
  connection: IntegrationConnection,
  allocationTypes: MappedEntity[],
): { mapped: number; total: number } | null => {
  const entities = connection.settings?.integrationEntitySettings ?? [];
  const total = entities.length;
  if (!total || !allocationTypes.length) return null;

  const offered = new Set(entities.map((entity) => entity.entityKey));
  // Two ways to miscount, and only counting the entities this integration
  // offers avoids both: several allocation types can point at one entity, and
  // one can point at an entity the settings no longer list. A clamp on the
  // result would hide the first and pass the second straight through.
  const mapped = new Set(
    allocationTypes
      .filter(
        (allocationType) =>
          allocationType.integrationSourceSystem === connection.sourceSystem &&
          allocationType.integrationEntity !== NOT_MAPPED &&
          offered.has(allocationType.integrationEntity),
      )
      .map((allocationType) => allocationType.integrationEntity),
  ).size;

  return { mapped, total };
};

export interface ConnectionMenuAction {
  labelKey: string;
  onClick: () => void;
  danger?: boolean;
}

/**
 * The kebab rows for one card. Connect, Reconnect and Manage are buttons on the
 * card itself, so what is left here is only the destructive pair.
 */
export const menuActions = (
  connection: IntegrationConnection,
  onDisconnect: (connection: IntegrationConnection) => void,
): ConnectionMenuAction[] => {
  if (connection.connectionStatus === ConnectionStatus.CONNECTED) {
    return [
      {
        labelKey: "Disconnect",
        onClick: () => onDisconnect(connection),
        danger: true,
      },
    ];
  }
  if (connection.connectionStatus === ConnectionStatus.DISCONNECTED) {
    return [
      {
        labelKey: "Remove",
        onClick: () => onDisconnect(connection),
        danger: true,
      },
    ];
  }
  return [];
};
